import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Typography,
  Container,
  Paper,
  Box,
} from '@mui/material';
import LinkField from '../common/components/LinkField';
import { useTranslation } from '../common/components/LocalizationProvider';
import SettingsMenu from './components/SettingsMenu';
import { formatNotificationTitle } from '../common/util/formatter';
import PageLayout from '../common/components/PageLayout';
import useFeatures from '../common/util/useFeatures';
import useSettingsStyles from './common/useSettingsStyles';
import { usePremiumTokens } from '../common/theme/usePremiumTokens';

const DeviceConnectionsPage = () => {
  const { classes } = useSettingsStyles();
  const t = useTranslation();
  const tokens = usePremiumTokens();

  const { id } = useParams();
  const features = useFeatures();

  // Safe token access with fallbacks
  const safeTokens = useMemo(() => ({
    radiusLg: tokens?.radius?.lg || '12px',
    surfaceCard: tokens?.surface?.card || '#161b22',
    borderSubtle: tokens?.border?.subtle || 'rgba(255, 255, 255, 0.04)',
    textPrimary: tokens?.text?.primary || '#f0f6fc',
  }), [tokens]);

  // Memoized styles to prevent Emotion cache issues
  const paperSx = useMemo(() => ({
    p: 3,
    borderRadius: safeTokens.radiusLg,
    backgroundColor: safeTokens.surfaceCard,
    border: `1px solid ${safeTokens.borderSubtle}`,
  }), [safeTokens]);

  const titleSx = useMemo(() => ({
    mb: 3,
    color: safeTokens.textPrimary,
  }), [safeTokens]);

  return (
    <PageLayout
      menu={<SettingsMenu />}
      breadcrumbs={['settingsTitle', 'sharedDevice', 'sharedConnections']}
    >
      <Container maxWidth={false} className={classes.container}>
        <Paper
          elevation={0}
          sx={paperSx}
        >
          <Typography
            variant="h6"
            sx={titleSx}
          >
            {t('sharedConnections')}
          </Typography>

          <Box display="flex" flexDirection="column" gap={2}>
            <LinkField
              endpointAll="/api/geofences"
              endpointLinked={`/api/geofences?deviceId=${id}`}
              baseId={id}
              keyBase="deviceId"
              keyLink="geofenceId"
              label={t('sharedGeofences')}
            />
            <LinkField
              endpointAll="/api/notifications"
              endpointLinked={`/api/notifications?deviceId=${id}`}
              baseId={id}
              keyBase="deviceId"
              keyLink="notificationId"
              titleGetter={(it) => formatNotificationTitle(t, it)}
              label={t('sharedNotifications')}
            />
            {!features.disableDrivers && (
              <LinkField
                endpointAll="/api/drivers"
                endpointLinked={`/api/drivers?deviceId=${id}`}
                baseId={id}
                keyBase="deviceId"
                keyLink="driverId"
                titleGetter={(it) => `${it.name} (${it.uniqueId})`}
                label={t('sharedDrivers')}
              />
            )}
            {!features.disableComputedAttributes && (
              <LinkField
                endpointAll="/api/attributes/computed"
                endpointLinked={`/api/attributes/computed?deviceId=${id}`}
                baseId={id}
                keyBase="deviceId"
                keyLink="attributeId"
                titleGetter={(it) => it.description}
                label={t('sharedComputedAttributes')}
              />
            )}
            {!features.disableSavedCommands && (
              <LinkField
                endpointAll="/api/commands"
                endpointLinked={`/api/commands?deviceId=${id}`}
                baseId={id}
                keyBase="deviceId"
                keyLink="commandId"
                titleGetter={(it) => it.description}
                label={t('sharedSavedCommands')}
              />
            )}
            {!features.disableMaintenance && (
              <LinkField
                endpointAll="/api/maintenance"
                endpointLinked={`/api/maintenance?deviceId=${id}`}
                baseId={id}
                keyBase="deviceId"
                keyLink="maintenanceId"
                label={t('sharedMaintenance')}
              />
            )}
          </Box>
        </Paper>
      </Container>
    </PageLayout>
  );
};

export default DeviceConnectionsPage;
